/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import * as moment from 'moment';
import { environment } from '@env/environment';
import { Logger } from '@app/core';
import { Calendar } from 'primeng/calendar';
const log = new Logger('DatePickerComponent');

@Component({
  selector: 'app-date-picker',
  templateUrl: './date-picker.component.html',
  styleUrls: ['./date-picker.component.scss'],
})
export class DatePickerComponent implements OnInit {
  @Input() FormControl: AbstractControl;
  @Input() yearsRange = 10;
  @Input() required = true;
  @Output() select = new EventEmitter();
  @ViewChild(Calendar) pCalendar: Calendar;
  yearsRangeFormatted: string;
  form: FormGroup;
  env = environment;
  currentVal: any;

  // https://stackoverflow.com/questions/34569094/what-is-the-angular-equivalent-to-an-angularjs-watch
  _minDate: string | Date = null;
  @Input() set minDate(value: string | Date) {
    log.debug('set minDate', value);

    const parsed = value ? moment(value, this.env.apiPipeDateFormat).toDate() : null;
    this._minDate = parsed;
    this._defaultDate = parsed; 

    setTimeout(() => {
      if (!this.FormControl || !this.form) return;

      this.currentVal = this.FormControl.value;

      if (this.currentVal) {
        // Only refresh display. DO NOT CLEAR THE VALUE.
        this.refreshDisplay();
        return;
      }
      
      if (!this.currentVal && parsed) {
        this.refreshDisplay();
        return;
      }
      
    }, 0);
  }


  get minDate(): string | Date {
    return this._minDate;
  }

  _maxDate: string | Date = null;
  @Input() set maxDate(value: string | Date) {
    if (!value || !moment(value, this.env.apiPipeDateFormat, true).isValid()) {
      this._maxDate = null;
    } else {
      this._maxDate = moment(value, this.env.apiPipeDateFormat).toDate();
    }
  }

  get maxDate(): string | Date {
    return this._maxDate;
  }

  _defaultDate: string | Date = null;
  @Input() set defaultDate(value: string | Date) {
    log.debug('set defaultDate');
    this._defaultDate = moment(value, this.env.apiPipeDateFormat).toDate() || null;
  }

  get defaultDate(): string | Date {
    return this._defaultDate;
  }

  en = {
    firstDayOfWeek: 1,
    dayNames: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
    dayNamesShort: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    dayNamesMin: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
    monthNames: [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ],
    monthNamesShort: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    today: 'Today',
    clear: 'Clear',
    weekHeader: 'Wk',
  };

  private justSelected = false;

  constructor(private formBuilder: FormBuilder) {}

  ngOnInit() {
    this.yearsRangeFormatted =
      moment().subtract(this.yearsRange, 'years').format('YYYY') +
      ':' +
      moment().add(this.yearsRange, 'years').format('YYYY');

    let required: any = [];
    if (this.required) {
      required = [Validators.required];
    }
    this.form = this.formBuilder.group({
      date_fake: ['', required],
    });

    if (this.FormControl.value) {
      this.currentVal = moment(this.FormControl.value).format(this.env.momentDateFormat);
      this.form.get('date_fake').setValue(this.currentVal);
    }     
    this.FormControl.valueChanges.subscribe((val) => {
      if (this.FormControl.value) {
        this.currentVal = moment(this.FormControl.value).format(this.env.momentDateFormat);
        this.form.get('date_fake').setValue(this.currentVal);
        
      } else {
        this.currentVal = null;
        this.form.get('date_fake').setValue(this.currentVal);
      }
    });
  }

  onSelect() {
    const date = moment(this.form.get('date_fake').value).format(this.env.apiPipeDateFormat);
    this.currentVal = date;
    this.FormControl.setValue(date);
    this.FormControl.markAsDirty();
    this.justSelected = true;

    // Праща дата-та към parent-елемента.
    // Когато се вика директивата може да се закачи "(onSelect)" event
    this.select.emit(date);
  }

  /**
   * Когато пипше проверява дали е налисана валидна дата.
   * При написана валидна дата сетва стойността и в истинския инпут.
   * При написана невалиднта дата запазва входа и маркира като невалиден.
   * Налага се ръчно обновяване на валидатора и  emitEvent: false, защото иначе зацикля.
   */
  onType() {
    console.log('onType');
    
    // Use setTimeout to allow onSelect to run first and set the flag
    setTimeout(() => {
      // Skip validation if this was just called after onSelect (calendar selection)
      if (this.justSelected) {
        this.justSelected = false;
        return;
      }

      const inputValue = this.form.get('date_fake').value;
      
      if (inputValue && moment(inputValue, this.env.apiPipeDateFormat, true).isValid()) {
        const date = moment(this.form.get('date_fake').value).format(this.env.apiPipeDateFormat);
        this.currentVal = date;
        this.FormControl.setValue(date);
        this.FormControl.markAsDirty();
        this.select.emit(date);
      } else if (inputValue && moment(inputValue, this.env.momentDateFormat, true).isValid()) {
        
      } else {
        // Empty field
        this.currentVal = null;
        this.FormControl.setValue(null, { emitEvent: false });
        if (this.required) {
          this.FormControl.setErrors({ required: true });
        }
      }
    }, 300);
  }

  /**
   * Public method to refresh the date display - can be called from parent components
   */
  public refreshDisplay(): void {
    if (this.FormControl && this.FormControl.value && this.form) {
      this.form.get('date_fake').setValue(moment(this.FormControl.value).format(this.env.momentDateFormat));
    }
  }
  /** 
   * Public method to navigate the calendar to the correct month and year
   */
  public navigateToDate(targetDate: Date): void {
    const calendarEl = document.querySelector('.p-datepicker') as any;

    if (calendarEl && targetDate instanceof Date && !isNaN(targetDate.getTime())) {
      const month = targetDate.getMonth();
      const year = targetDate.getFullYear();

      this._defaultDate = new Date(year, month, 1);
      this.refreshDisplay();
    }
  }
  public onCalendarShow(): void {
    // Check if minDate is defined and the calendar instance is available
    // if (this._minDate instanceof Date && this.pCalendar) {

    //     const targetDate = this._minDate as Date;

        
    //     this.pCalendar.defaultDate = targetDate; 

    //     // Programmatically set the current month and year on the component instance
    //     this.pCalendar.currentMonth = targetDate.getMonth();
    //     this.pCalendar.currentYear = targetDate.getFullYear();
        
    //     // Force PrimeNG to re-create the calendar grid for the new month/year
    //     this.pCalendar.createMonth(this.pCalendar.currentMonth, this.pCalendar.currentYear);
    // }
  }
}
